import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  isOfferFailed,
  isOfferSuccessfull,
  LouiseOffer,
} from '../../../../../types/Quotes';
// import OfferLogoWrapper from '../../components/default/OfferLogoWrapper';
import { IconActionEye, IconActionPlus } from 'wg-fe-ui';
import SingleOfferDetailModal from './SingleOfferDetailModal';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import DotLoaderWithText from '../../../../common/src/components/DotLoader';
import SingleOfferHeaderMobile from './SingleOfferHeaderMobile';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { useSelector } from 'react-redux';
import { useIsFetching } from 'react-query';
import { Button, Text } from '@wegroup/design-system';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import useIsNewRoute from '../../features/offers/hooks/useIsNewRoute';
import { useLouiseSelector } from '../../store/redux/store';
import useCloseOnline from '../../features/offers/hooks/useCloseOnline';

interface Props {
  offer: LouiseOffer;
  isComparing?: boolean;
  isSelected?: boolean;
  refetch: () => void;
  onAddToSelected: () => void;
  onOptionsChange: (arg0: string[]) => void;
}

const SingleOfferContainerMobile: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  isComparing,
  isSelected,
  refetch,
  onAddToSelected,
  onOptionsChange,
}) => {
  const { t } = useTranslation();
  const [viewDetails, setViewDetails] = useState<boolean>(false);
  const isNewRoute = useIsNewRoute();
  const shownProductsAmount = useLouiseSelector(
    (state) => state.offers.shownProductIds[offer.info.insurance_type]?.length,
  );
  const { handleCloseOnline } = useCloseOnline();

  const offerCombinationError = useSelector(
    (state: RootState) =>
      state.offers.offersInfo.offerCombinationError[offer.hash_id],
  );

  const sessionId = useSessionId();
  const isFetchingQuote = useIsFetching([
    sessionId,
    'quotes',
    offer.info.insurance_type.toLowerCase(),
    offer.hash_id,
  ]);

  return (
    <Wrapper
      isSelected={isSelected}
      data-test-id="ADVISORY-CONVO_OFFERS_SINGLE-OFFER_WRAPPER"
    >
      <SingleOfferDetailModal
        offer={offer}
        showModal={viewDetails}
        onClose={() => setViewDetails(false)}
        onOptionsChange={onOptionsChange}
      />
      <SingleOfferHeaderMobile offer={offer} />
      <BottomButtonContainer
        withCompare={isNewRoute && shownProductsAmount > 1}
      >
        <DefaultButton onClick={() => setViewDetails(true)}>
          <IconActionEye /> {t('View details')}
        </DefaultButton>
        {isOfferSuccessfull(offer) ? (
          isNewRoute ? (
            <AddButton onClick={() => handleCloseOnline(offer.id!)}>
              {t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_CLOSE-ONLINE')}
            </AddButton>
          ) : (
            <AddButton onClick={onAddToSelected}>
              <IconActionPlus color="white" />{' '}
              {isSelected
                ? t('Remove from selected offers')
                : t('Select offer')}
            </AddButton>
          )
        ) : isOfferFailed(offer) ? (
          <Button
            w="100%"
            h="100%"
            borderRadius="0"
            onClick={refetch}
            isDisabled={offerCombinationError}
            bg="danger.main"
            isLoading={isFetchingQuote > 0}
          >
            <Text textStyle="extraSmallBody">{t('Try again')}</Text>
          </Button>
        ) : (
          <AddButton>
            <DotLoaderWithText>{t('Calculating')}</DotLoaderWithText>
          </AddButton>
        )}
      </BottomButtonContainer>
    </Wrapper>
  );
};

const DefaultButton = styled.button`
  cursor: pointer;
  background-color: #f7f8fb;
  font-size: 1.2rem;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem;
  min-height: 4.5rem;

  &:active {
    background-color: ${({ theme }) => theme.gray[50]};
  }
  > svg {
    width: 1.8rem;
    height: 1.8rem;
    margin-right: 0.4rem;
  }
`;

const AddButton = styled(DefaultButton)`
  cursor: pointer;
  background-color: ${({ theme }) => theme.primary[500]};
  color: white;

  &:active {
    background-color: ${({ theme }) => theme.primary[400]};
  }
`;

const BottomButtonContainer = styled.div<{ withCompare?: boolean }>`
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(
    ${({ withCompare }) => (withCompare ? 3 : 2)},
    1fr
  );
  background-color: ${({ theme }) => theme.gray[50]};
  grid-gap: 1px; /* represents border between buttons */
  padding-top: 1px;
`;

const Wrapper = styled.div<{ isSelected?: boolean }>`
  background: white;
  border: 1px solid
    ${({ isSelected, theme }) =>
      isSelected ? theme.primary[500] : theme.gray[50]};
  border-radius: 6px;
  position: relative;
  width: 100%;

  ${mq.laptop} {
    display: none;
  }
`;

export default SingleOfferContainerMobile;
